import { useCallback, useEffect, useMemo, useState } from "react"
import { Archive, ArchiveRestore, Loader2, Search } from "lucide-react"
import type { AppShell } from "@/App"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"
import {
  createCatalogItem,
  listCatalogItems,
  patchCatalogItem,
  type CatalogItem,
  type CatalogKind,
} from "@/lib/api"
import { ProductRefCell } from "./ProductRefCell"
import { ProductSearch } from "./ProductSearch"
import { AutoLinkButton } from "./AutoLinkButton"

export interface CatalogField {
  key: string
  label: string
  type: "int" | "num" | "text" | "phase" | "bool"
}

export interface CatalogConfig {
  kind: CatalogKind
  intro: string
  roles: Array<{ value: string; label: string }>
  /** Campos técnicos editáveis do grupo (os que o motor usa). */
  fieldsFor: (role: string) => CatalogField[]
  /** Grupo com marca (equipamentos; instalação e mão de obra não têm). */
  hasBrand: (role: string) => boolean
}

const fmtEur = (v: unknown) =>
  Number(v).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/**
 * Catálogo AC/BC nos Mapeamentos com a mecânica do solar: pesquisa para
 * adicionar ao grupo, referência = link para o produto no CRM (trocar /
 * desligar), campos técnicos editáveis, ativar só com o que o motor precisa,
 * e «Remover» = arquivar (as propostas guardam o id da linha).
 */
export function ModuleCatalogTab({
  shell,
  config,
  onOpenProduct,
}: {
  shell: AppShell | null
  config: CatalogConfig
  onOpenProduct?: (productId: number) => void
}) {
  const { kind, roles } = config
  const [role, setRole] = useState<string>(roles[0].value)
  const [items, setItems] = useState<CatalogItem[]>([])
  const [loading, setLoading] = useState(false)
  const [savingId, setSavingId] = useState<number | null>(null)
  const [brandFilter, setBrandFilter] = useState("__all__")
  const [showArchived, setShowArchived] = useState(false)
  /** Linha a destacar depois de a escolher na pesquisa do catálogo. */
  const [highlightId, setHighlightId] = useState<number | null>(null)

  // Módulo mudou (BC AQ ↔ AQS) → volta ao primeiro grupo.
  useEffect(() => {
    setRole(roles[0].value)
  }, [roles])

  const reload = useCallback(async () => {
    if (!shell) return
    setItems(await listCatalogItems(shell, kind, role, showArchived))
  }, [shell, kind, role, showArchived])

  useEffect(() => {
    if (!shell) return
    let cancelled = false
    setLoading(true)
    listCatalogItems(shell, kind, role, showArchived)
      .then((rows) => !cancelled && setItems(rows))
      .catch(() => !cancelled && setItems([]))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [shell, kind, role, showArchived])

  useEffect(() => setBrandFilter("__all__"), [role])

  // Depois de saltar para uma linha (pesquisa no catálogo): traz à vista.
  useEffect(() => {
    if (highlightId == null) return
    const el = document.getElementById(`catalog-row-${highlightId}`)
    if (el) el.scrollIntoView({ block: "center", behavior: "smooth" })
  }, [highlightId, items])

  const roleLabel = useCallback(
    (r: string) => roles.find((x) => x.value === r)?.label ?? r,
    [roles],
  )
  const fields = config.fieldsFor(role)
  const withBrand = config.hasBrand(role)

  const brands = useMemo(() => {
    const set = new Set<string>()
    for (const i of items) if (i.brand) set.add(i.brand)
    return Array.from(set).sort()
  }, [items])

  const shown = useMemo(
    () => (brandFilter === "__all__" ? items : items.filter((i) => i.brand === brandFilter)),
    [items, brandFilter],
  )

  const toastError = (err: unknown) =>
    shell?.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : "Falha a guardar" })

  async function save(id: number, patch: Record<string, unknown>, opts: { reload?: boolean } = {}) {
    if (!shell) return
    setSavingId(id)
    try {
      const updated = await patchCatalogItem(shell, kind, { id, ...patch })
      if (opts.reload) await reload()
      else setItems((prev) => prev.map((r) => (r.id === id ? { ...r, ...updated, crm_product: r.crm_product } : r)))
    } catch (err) {
      toastError(err)
      await reload().catch(() => {})
    } finally {
      setSavingId(null)
    }
  }

  async function add(productId: number) {
    if (!shell) return
    try {
      const row = await createCatalogItem(shell, kind, role, productId)
      await reload()
      const price = Number(row.price_eur)
      shell.bridge.toast({
        variant: "success",
        description:
          `Adicionado (inativo). Preço do Odoo: ${price > 0 ? `${fmtEur(price)} €` : "sem custo — preencha"}.` +
          (row.missing?.length ? ` Falta: ${row.missing.join(", ")}.` : " Pode ativar."),
      })
    } catch (err) {
      toastError(err)
    }
  }

  const productsInGroup = new Set(items.filter((i) => !i.archived).map((i) => i.product_id).filter((x) => x != null))

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="text-sm text-muted-foreground">{config.intro}</p>
        <AutoLinkButton shell={shell} kind={kind} onDone={reload} />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {roles.map((r) => (
          <button
            key={r.value}
            onClick={() => setRole(r.value)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs transition",
              role === r.value
                ? "border-primary bg-primary/10 font-medium text-primary"
                : "border-border text-muted-foreground hover:bg-muted",
            )}
          >
            {r.label}
          </button>
        ))}
      </div>

      <CatalogFinder
        shell={shell}
        kind={kind}
        roles={roles}
        onPick={(r) => {
          if (r.archived) setShowArchived(true)
          setBrandFilter("__all__")
          setRole(r.role)
          setHighlightId(r.id)
        }}
      />

      <ProductSearch
        shell={shell}
        catalog={kind}
        placeholder="Adicionar produto do CRM ao grupo — pesquisa por nome ou SKU…"
        onPick={(p) => add(p.product_id)}
        taken={(p) => (productsInGroup.has(p.product_id) ? "neste grupo" : null)}
        hint={(p) => {
          const other = p.catalog_links?.find((l) => l.role !== role)
          return other ? `já em ${roleLabel(other.role)} (#${other.id})` : null
        }}
      />

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-muted-foreground">
          {shown.length} item{shown.length === 1 ? "" : "s"}
        </span>
        {brands.length > 1 && (
          <select
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            className="h-8 rounded-md border border-border bg-background px-2 text-xs"
          >
            <option value="__all__">Todas as marcas</option>
            {brands.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        )}
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
          Mostrar arquivados
        </label>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      <datalist id={`brands-${kind}`}>
        {brands.map((b) => (
          <option key={b} value={b} />
        ))}
      </datalist>

      <div className="overflow-x-auto rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-44">Referência</TableHead>
              <TableHead className="min-w-40">Nome público</TableHead>
              {withBrand && <TableHead className="w-24">Marca</TableHead>}
              {fields.map((f) => (
                <TableHead key={f.key} className={cn(f.type === "text" ? "w-24" : "w-[4.75rem] text-right")}>
                  {f.label}
                </TableHead>
              ))}
              <TableHead className="w-24 text-right">Preço (€)</TableHead>
              <TableHead className="w-16 text-center">Ativo</TableHead>
              <TableHead className="w-12 text-center">Arq.</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={6 + fields.length + (withBrand ? 1 : 0)} className="py-8 text-center text-sm text-muted-foreground">
                  Sem itens neste grupo. Adicione pela pesquisa acima.
                </TableCell>
              </TableRow>
            ) : (
              shown.map((r) => {
                const desc = (r.metadata ?? {})["description"]
                const blocked = !r.active && r.missing.length > 0
                return (
                  <TableRow
                    key={r.id}
                    id={`catalog-row-${r.id}`}
                    className={cn((!r.active || r.archived) && "opacity-60", highlightId === r.id && "bg-amber-50 ring-2 ring-inset ring-amber-300")}
                  >
                    <TableCell className="align-top">
                      <ProductRefCell
                        shell={shell}
                        catalog={kind}
                        sku={r.sku}
                        crmProduct={r.crm_product}
                        productId={r.product_id}
                        roleLabel={roleLabel}
                        onLink={(pid) => save(r.id, { product_id: pid }, { reload: true })}
                        onOpenProduct={onOpenProduct}
                      />
                    </TableCell>
                    <TableCell className="align-top text-sm">
                      <PublicName row={r} onOpenProduct={onOpenProduct} />
                      {typeof desc === "string" && desc.trim() && (
                        <span className="mt-0.5 block text-xs text-muted-foreground">{desc}</span>
                      )}
                      {r.archived ? (
                        <span className="mt-0.5 block text-[11px] text-muted-foreground">arquivado</span>
                      ) : r.missing.length > 0 ? (
                        <span className="mt-0.5 block text-[11px] text-amber-600">falta: {r.missing.join(", ")}</span>
                      ) : null}
                    </TableCell>
                    {withBrand && (
                      <TableCell className="align-top">
                        <Input
                          key={`brand-${r.id}-${r.brand ?? ""}`}
                          list={`brands-${kind}`}
                          defaultValue={r.brand ?? ""}
                          onBlur={(e) => {
                            const v = e.target.value.trim()
                            if (v !== (r.brand ?? "")) save(r.id, { brand: v || null })
                          }}
                          className="h-8 w-24 text-xs"
                        />
                      </TableCell>
                    )}
                    {fields.map((f) => (
                      <TableCell key={f.key} className={cn("align-top", f.type !== "text" && "text-right")}>
                        <FieldInput field={f} value={r[f.key]} onSave={(v) => save(r.id, { [f.key]: v })} />
                      </TableCell>
                    ))}
                    <TableCell className="align-top text-right">
                      <Input
                        key={`price-${r.id}-${r.price_eur}`}
                        type="number"
                        step="0.01"
                        defaultValue={Number(r.price_eur)}
                        onBlur={(e) => {
                          const v = Number(e.target.value)
                          if (Number.isFinite(v) && v !== Number(r.price_eur)) save(r.id, { price_eur: v })
                        }}
                        className="h-8 w-[5.5rem] text-right text-xs tabular-nums"
                      />
                    </TableCell>
                    <TableCell className="align-top text-center">
                      <div className="flex items-center justify-center gap-1" title={blocked ? `Falta: ${r.missing.join(", ")}` : undefined}>
                        <Switch
                          checked={r.active}
                          disabled={r.archived || blocked}
                          onCheckedChange={(v) => save(r.id, { active: v })}
                        />
                        {savingId === r.id && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                      </div>
                    </TableCell>
                    <TableCell className="align-top text-center">
                      <button
                        type="button"
                        onClick={() => save(r.id, { archived: !r.archived }, { reload: true })}
                        title={r.archived ? "Desarquivar (fica inativo)" : "Arquivar — sai do simulador; as propostas antigas mantêm-se"}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        {r.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                      </button>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-muted-foreground">
        A <b>referência</b> liga a linha ao produto do CRM (artigo do Odoo): é por ela que a
        encomenda vai para o Odoo e que o PDF mostra a foto e a ficha técnica. Produtos novos
        entram <b>inativos</b> com o custo do Odoo como preço; ativam-se quando têm o que o
        simulador precisa. Os valores aplicam-se ao sair do campo.
      </p>
    </div>
  )
}

/**
 * Pesquisa DENTRO do catálogo do módulo, em todos os grupos (nome do
 * catálogo, nome público, nome no CRM, SKU, id). Escolher abre o grupo da
 * linha e destaca-a. Diferente da barra «Adicionar produto do CRM».
 */
function CatalogFinder({
  shell,
  kind,
  roles,
  onPick,
}: {
  shell: AppShell | null
  kind: CatalogKind
  roles: Array<{ value: string; label: string }>
  onPick: (row: CatalogItem) => void
}) {
  const [query, setQuery] = useState("")
  const [all, setAll] = useState<CatalogItem[] | null>(null)
  const [loading, setLoading] = useState(false)

  // Carrega o catálogo inteiro (todos os grupos, com arquivados) na 1.ª pesquisa
  // — uma vez; escrever mais letras só filtra localmente.
  const wantsLoad = query.trim().length >= 2
  useEffect(() => {
    if (!shell || !wantsLoad || all) return
    let cancelled = false
    setLoading(true)
    listCatalogItems(shell, kind, "", true)
      .then((rows) => !cancelled && setAll(rows))
      .catch(() => !cancelled && setAll([]))
      .finally(() => setLoading(false))
    return () => {
      cancelled = true
    }
  }, [shell, kind, wantsLoad, all])

  const roleLabel = (r: string) => roles.find((x) => x.value === r)?.label ?? r
  const q = query.trim().toLowerCase()
  const results =
    q.length < 2 || !all
      ? []
      : all
          .filter((r) => roles.some((x) => x.value === r.role))
          .filter((r) =>
            [r.name, r.sku, r.crm_product?.name, r.crm_product?.alt_name, r.odoo_name, `#${r.id}`]
              .some((v) => typeof v === "string" && v.toLowerCase().includes(q)),
          )
          .slice(0, 30)

  return (
    <div className="relative">
      <div className="flex items-center gap-2 rounded-md border border-dashed border-border px-3 py-2">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Procurar no catálogo (todos os grupos) — nome, SKU ou #id…"
          autoComplete="off"
          spellCheck={false}
          className="border-0 p-0 shadow-none focus-visible:ring-0"
        />
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
      {q.length >= 2 && all && (
        <div className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border border-border bg-popover shadow-md">
          {results.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">Nada no catálogo com «{query.trim()}».</div>
          ) : (
            results.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => { onPick(r); setQuery("") }}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">{r.crm_product?.alt_name || r.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {roleLabel(r.role)} · #{r.id}
                    {r.sku ? ` · ${r.sku}` : " · sem referência"}
                    {r.archived ? " · arquivado" : !r.active ? " · inativo" : ""}
                  </span>
                </span>
                <span className="shrink-0 text-xs text-accent">abrir</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Nome que o simulador e o PDF mostram: o «nome alternativo» do produto do
 * CRM ligado, quando definido (como no solar); senão o nome do catálogo. Muda-
 * -se no produto do CRM — o link leva lá.
 */
function PublicName({
  row,
  onOpenProduct,
}: {
  row: CatalogItem
  onOpenProduct?: (productId: number) => void
}) {
  const crm = row.crm_product
  const alt = crm?.alt_name?.trim() || null
  const sameAsCrm = crm && crm.name.trim().toLowerCase() === row.name.trim().toLowerCase()
  return (
    <>
      <span>{alt ?? row.name}</span>
      {alt ? (
        <span className="mt-0.5 block text-[11px] text-muted-foreground">
          nome alternativo do CRM · catálogo: {row.name}
        </span>
      ) : crm && !sameAsCrm ? (
        <span className="mt-0.5 block text-[11px] text-muted-foreground">
          no CRM: {crm.name} ·{" "}
          <button type="button" onClick={() => onOpenProduct?.(crm.id)} className="text-accent hover:underline">
            definir nome alternativo
          </button>
        </span>
      ) : null}
    </>
  )
}

function FieldInput({
  field,
  value,
  onSave,
}: {
  field: CatalogField
  value: unknown
  onSave: (v: unknown) => void
}) {
  if (field.type === "phase") {
    return (
      <select
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onSave(e.target.value || null)}
        className="h-8 rounded-md border border-border bg-background px-1 text-xs"
      >
        <option value="">—</option>
        <option value="mono">Mono</option>
        <option value="tri">Tri</option>
      </select>
    )
  }
  if (field.type === "bool") {
    return <Switch checked={value === true} onCheckedChange={(v) => onSave(v)} />
  }
  const current = value == null ? "" : String(value)
  return (
    <Input
      key={`${field.key}-${current}`}
      type={field.type === "text" ? "text" : "number"}
      step={field.type === "num" ? "0.01" : "1"}
      defaultValue={current}
      onBlur={(e) => {
        const raw = e.target.value.trim()
        if (raw === current) return
        if (field.type === "text") return onSave(raw || null)
        if (raw === "") return onSave(null)
        const n = Number(raw.replace(",", "."))
        if (Number.isFinite(n)) onSave(field.type === "int" ? Math.round(n) : n)
      }}
      className={cn("h-8 px-2 text-xs tabular-nums", field.type === "text" ? "w-20" : "w-[4.75rem] text-right")}
    />
  )
}
